import { CheckCircle, Copy, ExternalLink } from 'lucide-react'
import Image from 'next/image'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { facebookAdsGetCreative } from 'sprites-types/workflow/tools/facebookAdsGetCreative'
import { type ContextAction, ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'

function FacebookAdsGetCreativeDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsGetCreative, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const creative = result.creative

  const contextActions: ContextAction[] = [
    {
      id: 'copy-id',
      label: 'Copy Creative ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(creative.id),
    },
    ...(creative.link_url
      ? [
          {
            id: 'open-link',
            label: 'Open Link URL',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(creative.link_url!, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">Ad Creative Details</div>
            </div>

            <div className="space-y-3">
              {/* Creative ID */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="mb-1 text-xs font-medium text-gray-500">Creative ID</div>
                <div className="text-sm text-gray-900">{creative.id}</div>
              </div>

              {/* Creative Name */}
              {creative.name && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Name</div>
                  <div className="text-sm text-gray-900">{creative.name}</div>
                </div>
              )}

              {/* Title/Headline */}
              {creative.title && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Title/Headline</div>
                  <div className="text-sm text-gray-900">{creative.title}</div>
                </div>
              )}

              {/* Body Text */}
              {creative.body && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Body Text</div>
                  <div className="text-sm text-gray-900">{creative.body}</div>
                </div>
              )}

              {/* Call to Action */}
              {creative.call_to_action_type && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Call to Action</div>
                  <div className="text-sm text-gray-900">{creative.call_to_action_type}</div>
                </div>
              )}

              {/* Link URL */}
              {creative.link_url && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Link URL</div>
                  <a
                    href={creative.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <span className="break-all">{creative.link_url}</span>
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
              )}

              {/* Image Hash */}
              {creative.image_hash && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Image Hash</div>
                  <div className="font-mono text-sm text-gray-900">{creative.image_hash}</div>
                </div>
              )}

              {/* Video */}
              {creative.video_id && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="mb-1 text-xs font-medium text-gray-500">Video ID</div>
                  <div className="text-sm text-gray-900">{creative.video_id}</div>
                  {creative.thumbnail_url && (
                    <div className="relative mt-2 aspect-video w-full overflow-hidden rounded-lg">
                      <Image
                        src={creative.thumbnail_url}
                        alt="Video thumbnail"
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsGetCreative(_nodeExecution: NodeExecution): Promise<void> {
  throw new Error('Download not implemented for Facebook Ads Get Creative')
}

function facebookAdsGetCreativeToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsGetCreative, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return 'Error: Failed to parse creative data'
  }

  const { creative } = parseResult.data

  return `# Facebook Ad Creative Details

- **Creative ID:** ${creative.id}
${creative.name ? `- **Name:** ${creative.name}` : ''}
${creative.title ? `- **Title:** ${creative.title}` : ''}
${creative.body ? `- **Body:** ${creative.body}` : ''}
${creative.call_to_action_type ? `- **Call to Action:** ${creative.call_to_action_type}` : ''}
${creative.link_url ? `- **Link URL:** ${creative.link_url}` : ''}
${creative.image_hash ? `- **Image Hash:** ${creative.image_hash}` : ''}
${creative.video_id ? `- **Video ID:** ${creative.video_id}` : ''}
${creative.thumbnail_url ? `- **Thumbnail URL:** ${creative.thumbnail_url}` : ''}

---
`
}

export const FacebookAdsGetCreativeDisplay = createWorkflowToolComponent(
  FacebookAdsGetCreativeDisplayComponent,
  downloadFacebookAdsGetCreative,
  facebookAdsGetCreativeToMarkdown
)
